/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { PrRunsData, parsePrRunsResponse } from './claudeReviewQuality';
import { PeriodScope } from './claudeReviewQualityJoin';

/** The server-filterable run dimensions (team stays a client-side projection). */
export type RunQueryFilters = {
  org?: string;
  repo?: string;
  pr?: number;
  author?: string;
  trigger?: string;
  model?: string;
  job?: string;
};

/**
 * Lazily reads the union of runs matching `filters` inside the displayed
 * period (PLAT-1819 run drill) — fetched only while a drill-down is open, so
 * the panel's initial load pays nothing. Period boundaries resolve server-side
 * with the same helpers the period views use.
 *
 * Same degradation contract as the sibling hooks: a 404 stays silent (a
 * server one deploy behind has no /runs route), anything else is REPORTED via
 * errorApi, never swallowed.
 */
export const useGroupRuns = (
  target: { filters: RunQueryFilters; scope: PeriodScope } | undefined,
): { data?: PrRunsData } => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const errorApi = useApi(errorApiRef);
  const [data, setData] = useState<PrRunsData>();

  const key = target ? JSON.stringify([target.filters, target.scope]) : undefined;

  useEffect(() => {
    if (!target) {
      setData(undefined);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('claude-metrics');
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(target.filters)) {
          if (v !== undefined) params.set(k, String(v));
        }
        const s = target.scope;
        params.set('granularity', s.granularity);
        if (s.granularity === 'week') {
          params.set('isoYear', String(s.isoYear));
          params.set('isoWeek', String(s.isoWeek));
        } else if (s.granularity === 'day') {
          params.set('day', s.day);
          params.set('tz', s.tz);
        } else {
          params.set('month', s.month);
          params.set('tz', s.tz);
        }
        const res = await fetchApi.fetch(`${base}/runs?${params}`);
        if (res.status === 404) return;
        if (!res.ok) throw new Error(`request failed (${res.status})`);
        const parsed = parsePrRunsResponse(await res.json());
        if (!parsed) throw new Error('runs payload unreadable');
        if (active) setData(parsed);
      } catch (e) {
        if (active) setData(undefined);
        errorApi.post(
          new Error(
            `Claude usage: run drill unavailable (${
              e instanceof Error ? e.message : e
            })`,
          ),
        );
      }
    })();
    return () => {
      active = false;
    };
    // key encodes the whole target; the object identity changes per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoveryApi, fetchApi, errorApi, key]);

  return { data };
};
